import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Sun, Moon, Languages, Radio } from 'lucide-react';
import { useTheme } from '../theme/ThemeProvider';
import { API } from '../config/api';

/**
 * DATAPRO "Full Stage" global topbar strip.
 *
 * A persistent, thin bar mounted at the very top of the app on every
 * authenticated screen. Hosts the three always-visible controls
 * required by the design blueprint:
 *   - Theme toggle (Dark / Light)
 *   - Language toggle (ES / EN)
 *   - Global "LIVE" session indicator (badge + link to /app when active)
 *
 * Intentionally minimal so it stacks cleanly ABOVE the existing white
 * header without conflicting with per-page navigation. Later phases
 * will fold this into the redesigned Topbar+Sidebar shell.
 */

// Routes where the strip must NOT appear (public marketing pages
// keep their own aesthetic until Phase 3).
const HIDDEN_ROUTES = new Set(['/', '/login', '/pricing']);

const POLL_INTERVAL_MS = 30_000;

const GlobalTopbar = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [liveActive, setLiveActive] = useState(false);

  const isHidden = HIDDEN_ROUTES.has(location.pathname)
    || location.pathname.startsWith('/payment');

  const toggleLanguage = useCallback(() => {
    const next = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(next);
  }, [i18n]);

  useEffect(() => {
    if (isHidden) return undefined;
    let cancelled = false;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/speedhive/live/auto-status`, {
          withCredentials: true,
        });
        if (cancelled) return;
        // Backend returns { active: bool, sessions: [...] } — accept either shape.
        const active = !!(res.data?.active ?? res.data?.is_active ?? (res.data?.sessions?.length > 0));
        setLiveActive(active);
      } catch {
        if (!cancelled) setLiveActive(false);
      }
    };

    check();
    const id = window.setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isHidden, location.pathname]);

  if (isHidden) return null;

  const isDark = theme === 'dark';
  const langLabel = i18n.language === 'es' ? 'ES' : 'EN';
  const nextLangLabel = i18n.language === 'es' ? 'EN' : 'ES';

  return (
    <div
      data-testid="global-topbar"
      className="sticky top-0 z-50 w-full border-b border-neutral-800 bg-neutral-950/95 text-white backdrop-blur supports-[backdrop-filter]:bg-neutral-950/80"
      style={{ borderBottomColor: 'var(--dp-border)' }}
    >
      <div className="mx-auto flex h-9 max-w-full items-center justify-between gap-2 px-3 md:px-6">
        {/* LEFT: Live status pill */}
        <div className="flex items-center gap-2">
          {liveActive ? (
            <Link
              to="/app"
              data-testid="global-live-badge-active"
              className="group flex items-center gap-2 rounded-full border border-red-600/40 bg-red-600/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-red-500 transition-colors hover:bg-red-600/20 active:scale-95"
              title={t('topbar.live_active')}
            >
              <span className="dp-live-dot inline-block h-2 w-2 rounded-full bg-red-500" />
              <span>{t('nav.live')}</span>
              <Radio className="h-3 w-3" aria-hidden="true" />
            </Link>
          ) : (
            <span
              data-testid="global-live-badge-idle"
              className="flex items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-500"
              title={t('topbar.no_live')}
            >
              <span className="inline-block h-2 w-2 rounded-full bg-neutral-600" />
              <span>{t('nav.live')}</span>
            </span>
          )}
        </div>

        {/* RIGHT: Theme + Language toggles */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="language-toggle-button"
            onClick={toggleLanguage}
            aria-label={t('topbar.language')}
            title={`${t('topbar.language')}: ${langLabel} → ${nextLangLabel}`}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900 px-2 text-xs font-bold uppercase tracking-wider text-neutral-200 transition-colors hover:border-red-600/50 hover:text-white active:scale-95"
          >
            <Languages className="h-3.5 w-3.5" aria-hidden="true" />
            <span data-testid="language-toggle-label">{langLabel}</span>
          </button>

          <button
            type="button"
            data-testid="theme-toggle-button"
            onClick={toggleTheme}
            aria-label={isDark ? t('topbar.theme_light') : t('topbar.theme_dark')}
            title={isDark ? t('topbar.theme_light') : t('topbar.theme_dark')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-800 bg-neutral-900 text-neutral-200 transition-colors hover:border-red-600/50 hover:text-white active:scale-95"
          >
            {isDark ? (
              <Sun className="h-4 w-4" data-testid="theme-icon-sun" aria-hidden="true" />
            ) : (
              <Moon className="h-4 w-4" data-testid="theme-icon-moon" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalTopbar;
